import {INetworkComponent} from "../../network/types/INetworkComponent";
import {Network} from "../../network/Network";
import {HttpStatus} from "../../network/status/HttpStatus";
import {AccountService} from "../account/AccountService";
import {IEntityModel} from "../../models/entity/IEntityModel";
import {Models} from "../../models/Models";
import {Services} from "../Services";
import {WebSocketPanelEventName} from "../../network/socket/names/WebSocketPanelEventName";
import {EntityType} from "../../models/entity/types/EntityType";
import {ISiteModel} from "../../models/site/ISiteModel";
import {IAccountModel} from "../../models/account/IAccountModel";
import {NetworkHeaders} from "../../network/types/NetworkHeaders";
import {SiteService} from "../site/SiteService";
import {ProductType} from "../../models/product/ProductType";
import {config} from "../../config";
import {Resources} from "../../resources/Resources";
import {currencyByCode} from "../../models/currency/Currencies";
import {
    SedestralStorage
} from "../../sedestral/framework/memory/SedestralStorage";
import {IOfferProductModel} from "../../models/offer/product/IOfferProductModel";
import {SedestralMemory} from "../../sedestral/framework/memory/SedestralMemory";
import {AccountEvent} from "../../models/account/event/AccountEvent";
import {IAccountEventIncoming} from "../../models/account/event/IAccountEventIncoming";
import {ICommunityModel} from "../../models/community/ICommunityModel";

export class EntityService {

    public static activeEntities: { product: string, entity: IEntityModel }[];
    public static activeSites: { product: string, site: ISiteModel }[];

    public static activeOfferProduct: IOfferProductModel;
    public static activeOfferIsFailureToPay: boolean = false;
    public static activeSiteCbExpiringSoon: boolean = false;
    public static activeOfferEverPaid: boolean = false;
    public static activeOfferExpired: boolean = false;
    public static activeOfferLocked: boolean = false;
    public static feedbackRequested: boolean = false;
    public static accountEvents: { [key: string]: AccountEvent[] } = {};

    public static activeCommunity: ICommunityModel;

    public static _activeWebPushTokenValue: string;
    private static _activeWebPushToken: string = "_activeWebPushToken";

    /**
     * get and set
     */


    static addAccountEvent(accountEvent: IAccountEventIncoming): void {
        if (!this.accountEvents[accountEvent.objectId]) {
            this.accountEvents[accountEvent.objectId] = [];
        }
        this.accountEvents[accountEvent.objectId].push(accountEvent.event);
    }

    static get activeEntity(): IEntityModel {
        this.initActives();
        return EntityService.activeEntities.find(value => value.product == config.product)?.entity;
    }

    static set activeEntity(entity: IEntityModel) {
        EntityService.activeEntities.push({product: config.product, entity});
    }

    static get activeSite(): ISiteModel {
        this.initActives();
        return EntityService.activeSites.find(value => value.product == config.product)?.site;
    }

    static set activeSite(entity: ISiteModel) {
        EntityService.activeSites.push({product: config.product, site: entity});
        if (entity) {
            Resources.currency = currencyByCode(entity.currency);
        }
    }


    static get activeWebPushToken(): string {
        return this._activeWebPushTokenValue;
    }

    static set activeWebPushToken(token: string) {
        this._activeWebPushTokenValue = token;
        SedestralStorage.setItem(this._activeWebPushToken, this._activeWebPushTokenValue);
    }

    static get activeToken(): string {
        return SedestralStorage.getItem(`_activeToken` + config.product + config.environment);
    }

    static set activeToken(token: string) {
        SedestralStorage.setItem(`_activeToken` + config.product + config.environment, token);
    }

    static clearActiveToken() {
        SedestralStorage.removeItem(`_activeToken` + config.product + config.environment);
    }

    static get saveActiveToken(): string {
        return SedestralMemory.getItem(`_saveActiveToken` + config.product + config.environment);
    }

    static set saveActiveToken(token: string) {
        SedestralMemory.setItem(`_saveActiveToken` + config.product + config.environment, token);
    }

    static clearSaveActiveToken() {
        SedestralMemory.removeItem(`_saveActiveToken` + config.product + config.environment);
    }

    static get activeTokenCookie(): string | null {
        this.initActives();
        return SedestralStorage.getCookie(`_activeTokenCookie` + config.environment + config.product);
    }

    static set activeTokenCookie(token: string) {
        this.initActives();
        SedestralStorage.setCookie(`_activeTokenCookie` + config.environment + config.product, token, "." + config.domainParent);
    }

    static clearTokenCookie() {
        SedestralStorage.deleteCookie(`_activeTokenCookie` + config.environment + config.product);
    }

    public static dispose(): void {
    }

    public static initActives(): void {
        if (!EntityService.activeEntities)
            EntityService.activeEntities = [];

        if (!EntityService.activeSites)
            EntityService.activeSites = [];
    }

    public static init(): void {
        this.initActives();

        Services.beforeInit(this);
        this._activeWebPushTokenValue = SedestralStorage.getItem(this._activeWebPushToken);
        Services.on(this, ProductType.PANEL, WebSocketPanelEventName.ENTITY_UPDATE, (data) => {
            let entity: IEntityModel = data;
            if (Models.isAccountModel(entity)) {
                AccountService.store(entity as IAccountModel);
            }
        });

        window["EntityService"] = this;
    }

    /**
     * http
     */

    public static async updateAvatar(accountId: string, files: File[], component?: INetworkComponent): Promise<IEntityModel> {
        let request = await Network.postFormData(ProductType.PANEL, `/account/avatar/update`, {
            files: files,
            accountId: accountId
        }, component);

        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }

        return undefined;
    }

    /**
     * get
     */

    public static getEntity(type: EntityType, id: string): IEntityModel {
        if (type == EntityType.ACCOUNT) {
            return AccountService.accounts.filter(value => value.id == id)[0];
        }
    }

    public static getType(entity: IEntityModel): EntityType {
        return Models.isContactModel(entity) ? EntityType.CONTACT : EntityType.ACCOUNT;
    }

    /**
     * headers
     */

    public static headersToken(headers, saveCookie: boolean = true) {
        if (headers !== undefined && headers[NetworkHeaders.SESSION_HEADER] != undefined) {
            EntityService.activeToken = headers[NetworkHeaders.SESSION_HEADER];

            if (saveCookie) {
                EntityService.activeTokenCookie = headers[NetworkHeaders.SESSION_HEADER];
            }

        }
    }

    /**
     * store
     */

    public static storeContext(entity: IEntityModel, site: ISiteModel) {
        this.activeEntity = this.store(entity, entity.type);
        this.activeSite = SiteService.store(site);
    }

    public static storeAll(entities: IEntityModel[], type?: EntityType): IEntityModel[] {
        for (let key in entities)
            entities[key] = this.store(entities[key], type);

        return Services.storeAll(entities);
    }

    public static store(entity: IEntityModel, type?: EntityType): IEntityModel {
        if (type == undefined) {
             if (Models.isAccountModel(entity))
                // @ts-ignore
            {
                entity = AccountService.store(entity);
            }
        } else {

            if (type == EntityType.ACCOUNT) {
                // @ts-ignore
                entity = AccountService.store(entity);
            }
        }


        return entity;
    }

}